import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Check, X, ArrowRight, ShieldCheck, Building2, Users, Plus, Minus, Lock, Archive, Globe, LayoutGrid } from "lucide-react";

// ── Image assets from Gamma CDN ───────────────────────────────────────────────
const IMG = {
  hero:     "https://cdn.gamma.app/s03ytecqnn6ry6t/bc2105db3683425c95d36926326e7b43/SLHOWrUElnxvYh_Che-a0.jpg",
  mosaic1:  "https://cdn.gamma.app/s03ytecqnn6ry6t/321551d954f34c60bf330f93f0c67302/1Rqss5IMbcDJ-0XNX0AvL.jpg",
  mosaic3:  "https://cdn.gamma.app/s03ytecqnn6ry6t/37ea0f271d6d487482becbcc3ee31b59/3Ucm1t_efiIP6BONJAStS.jpg",
  story:    "https://cdn.gamma.app/s03ytecqnn6ry6t/0d56ecd14ab64f28a688339dab91d534/Z13MQVk5nTCcJPcbxxI7Z.jpg",
  founding: "https://cdn.gamma.app/s03ytecqnn6ry6t/a15c2bf69be14c50a247bba086187687/TK_4YwN1C8UkGkez_fZnE.jpg",
  platform: "https://cdn.gamma.app/s03ytecqnn6ry6t/generated-images/97uiITXLFZJAciGm.png",
  matters:  "https://cdn.gamma.app/s03ytecqnn6ry6t/8814f863339c4855bd2c2e40449c428b/aElYhkSYZwA1ZwQ9W-ttn.jpg",
  cta:      "https://cdn.gamma.app/s03ytecqnn6ry6t/d49a2197d0b84507b24970023b43d717/moeAHGTP52mnhODSu3dN6.webp",
  sovereignty: "https://static.prod-images.emergentagent.com/jobs/66b07bb2-6428-4ff8-b65b-1b59d7598e97/images/64ddbedee4ae3a76bcfeebc28b84cb5ec37b0428eea274ca4279dd0b36e39f5e.jpeg",
};

// Lightened dark tones (previously near-black, now brighter for on-screen readability)
const DARK_GREEN = "#173328";
const DARK_BLUE  = "#26405A";

// ── Scroll-reveal hook ────────────────────────────────────────────────────────
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Reveal wrapper ────────────────────────────────────────────────────────────
function Reveal({ children, className = "", delay = 0, from = "bottom" }) {
  const [ref, visible] = useReveal();
  const translate =
    from === "left"  ? "translateX(-28px)" :
    from === "right" ? "translateX(28px)"  : "translateY(24px)";
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : translate,
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ── Confidential watermark ────────────────────────────────────────────────────
function Conf() {
  return (
    <p className="mt-16 text-center text-xs uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.25)" }}>
      Frog Lake First Nation · Bear Valley Solutions · Confidential
    </p>
  );
}

// ── Section label chip ────────────────────────────────────────────────────────
function Chip({ children, light = false }) {
  return (
    <span className={`inline-block text-sm font-bold uppercase tracking-widest px-4 py-1.5 mb-5 ${light ? "bg-[#FFC72C] text-black" : "bg-[#005696] text-white"}`}>
      {children}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERACTIVE PLATFORM DIAGRAM - hub-and-spoke, synced with the app list on hover
// ═══════════════════════════════════════════════════════════════════════════════
const PLATFORM_APPS = [
  { key: "hub",     name: "Hub Login Framework" },
  { key: "partner", name: "Partner Management" },
  { key: "grants",  name: "Grants Management" },
  { key: "invoice", name: "Invoice and Financial" },
  { key: "econdev", name: "Economic Development" },
  { key: "housing", name: "Housing" },
  { key: "library", name: "Library and Archives" },
  { key: "equine",  name: "Equine Centre" },
];

// 3x3 grid layout (row-major); null marks the center hub node
const DIAGRAM_GRID = ["equine", "hub", "partner", "library", null, "grants", "housing", "econdev", "invoice"];

const POS = {
  equine:  [16.5, 16.5], hub:     [50, 16.5], partner: [83.5, 16.5],
  library: [16.5, 50],   center:  [50, 50],   grants:  [83.5, 50],
  housing: [16.5, 83.5], econdev: [50, 83.5], invoice: [83.5, 83.5],
};

function PlatformDiagram({ hovered, setHovered }) {
  const isActive = (key) => hovered === key;
  return (
    <div className="relative w-full aspect-square" data-testid="platform-diagram">
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: "visible" }}>
        {Object.entries(POS).filter(([k]) => k !== "center").map(([key, [x, y]]) => {
          const active = isActive(key);
          const dotX = 50 + (x - 50) * 0.6;
          const dotY = 50 + (y - 50) * 0.6;
          return (
            <g key={key}>
              <line
                x1={50} y1={50} x2={x} y2={y}
                stroke={active ? "#FFC72C" : "#0F2419"}
                strokeOpacity={active ? 1 : 0.22}
                strokeWidth={active ? 1.5 : 0.7}
                style={{ transition: "all 0.3s ease" }}
              />
              <circle
                cx={dotX} cy={dotY} r={active ? 1.7 : 1.1}
                fill={active ? "#FFC72C" : "#C0392B"}
                style={{ transition: "all 0.3s ease" }}
              />
            </g>
          );
        })}
      </svg>

      {DIAGRAM_GRID.map((key, i) => {
        const isCenter = key === null;
        const [x, y] = isCenter ? POS.center : POS[key];
        const app = isCenter ? null : PLATFORM_APPS.find((a) => a.key === key);
        const label = isCenter ? "froglake.ca" : app.name;
        const active = isCenter ? true : isActive(key);
        return (
          <div
            key={i}
            data-testid={isCenter ? "platform-diagram-hub" : `platform-diagram-node-${key}`}
            onMouseEnter={() => !isCenter && setHovered(key)}
            onMouseLeave={() => !isCenter && setHovered(null)}
            className={`absolute flex items-center justify-center text-center rounded-full border-2 font-heading uppercase transition-all duration-300 ${!isCenter ? "cursor-pointer" : ""}`}
            style={{
              left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)",
              width: isCenter ? "31%" : "27%",
              height: isCenter ? "31%" : "27%",
              backgroundColor: isCenter ? DARK_GREEN : active ? "#FFC72C" : "#FFFFFF",
              borderColor: isCenter ? "#FFC72C" : active ? "#FFC72C" : "#DAD4C6",
              color: isCenter ? "#FFFFFF" : active ? "#000000" : "#3A3A3A",
              fontSize: isCenter ? "clamp(0.7rem, 1.7vw, 1rem)" : "clamp(0.6rem, 1.35vw, 0.85rem)",
              fontWeight: active || isCenter ? 800 : 700,
              padding: "5%",
              lineHeight: 1.15,
              boxShadow: active && !isCenter ? "0 10px 28px rgba(255,199,44,0.65)" : "0 2px 6px rgba(0,0,0,0.08)",
              zIndex: 10,
            }}
          >
            {label}
          </div>
        );
      })}
    </div>
  );
}

function PlatformAppsSection() {
  const [hovered, setHovered] = useState(null);
  return (
    <section className="bg-[#F9F7F4] py-24 px-4">
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-[#0F2419] uppercase mb-5">
            Platform Applications, One Login
          </h2>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto leading-relaxed">
            Each application connects to froglake.ca through one login. Low one-time setup, then monthly access at founding-customer rates.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <Reveal from="left">
            <div className="space-y-2.5">
              {PLATFORM_APPS.map((app) => {
                const active = hovered === app.key;
                return (
                  <div
                    key={app.key}
                    data-testid={`platform-app-${app.key}`}
                    onMouseEnter={() => setHovered(app.key)}
                    onMouseLeave={() => setHovered(null)}
                    className={`flex items-center gap-3 px-5 py-4 cursor-pointer transition-all duration-300 ${
                      active
                        ? "bg-[#FFC72C] border-4 border-[#FFC72C] scale-[1.04] shadow-[0_10px_28px_rgba(255,199,44,0.55)]"
                        : "bg-white border-2 border-gray-200 hover:border-[#005696]/50"
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 transition-colors ${active ? "bg-black" : "bg-[#005696]"}`} />
                    <span className={`text-lg transition-all ${active ? "text-black font-bold" : "text-gray-700 font-semibold"}`}>
                      {app.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal from="right" delay={150}>
            <PlatformDiagram hovered={hovered} setHovered={setHovered} />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DATA SOVEREIGNTY & INFRASTRUCTURE - two-phase timeline + architecture diagram
// ═══════════════════════════════════════════════════════════════════════════════
const INFRA_PHASES = [
  {
    key: "phase1",
    label: "Phase 1 - Now",
    title: "Secure Canadian Cloud",
    bullets: [
      "Encrypted, redundant hosting on Canadian-based cloud infrastructure",
      "Full data protection and compliance from day one",
      "Development continues immediately - no delays waiting on hardware",
    ],
  },
  {
    key: "phase2",
    label: "Phase 2 - On-Nation",
    title: "Local, On-Site Infrastructure",
    bullets: [
      "A single on-site server rack hosted on Frog Lake land",
      "Reliable fiber internet plus backup connectivity for uptime",
      "Houses all Nation data, archives, and sensitive records under local control",
      "Installed and maintained together with trusted local IT and infrastructure partners",
    ],
  },
];

function InfraTimeline() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-stretch" data-testid="infra-phase-timeline">
      {INFRA_PHASES.map((phase, i) => (
        <React.Fragment key={phase.key}>
          <div
            data-testid={`infra-phase-card-${phase.key}`}
            className="group border-4 border-transparent bg-white/[0.06] border-t-[#FFC72C] p-7 transition-all duration-300 hover:bg-white/[0.16] hover:border-[#FFC72C] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.35)]"
          >
            <ShieldCheck className="w-8 h-8 text-[#FFC72C] mb-4 transition-transform duration-300 group-hover:scale-110" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#FFC72C]">{phase.label}</span>
            <h3 className="font-heading text-2xl uppercase text-white mt-1 mb-4">{phase.title}</h3>
            <ul className="space-y-2.5">
              {phase.bullets.map((b, j) => (
                <li key={j} className="flex gap-2.5 text-white/85 text-base leading-relaxed group-hover:text-white transition-colors duration-300">
                  <Check className="w-4 h-4 text-[#FFC72C] shrink-0 mt-1" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          {i === 0 && (
            <div className="flex flex-col items-center justify-center px-2" data-testid="infra-timeline-arrow">
              <ArrowRight className="hidden md:block w-8 h-8 text-[#FFC72C]" />
              <ChevronDown className="md:hidden w-8 h-8 text-[#FFC72C]" />
              <span className="text-white/60 text-xs text-center max-w-[8rem] mt-2 leading-snug">
                Transition when the Nation is ready - no fixed deadline
              </span>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

const INFRA_TECH_DETAILS = [
  "Data encrypted at rest and in transit across both hosting phases",
  "Redundant cloud infrastructure with automatic failover during Phase 1",
  "On-site rack designed for resilient fiber connectivity with backup uptime protection",
  "Centralized, Nation-governed repository for archives and sensitive records in Phase 2",
  "Coordinated installation and ongoing maintenance with Frog Lake's existing IT providers",
];

function InfraArchitectureDiagram() {
  const [showTech, setShowTech] = useState(false);
  return (
    <div data-testid="infra-architecture-diagram">
      <div className="flex flex-col items-center">
        <span className="text-xs font-bold uppercase tracking-widest text-white/50 mb-3">Application Layer</span>
        <div className="flex flex-wrap justify-center gap-3">
          {["froglake.ca Website", "Nation Management Platform", "Data Archives & Records"].map((label) => (
            <div
              key={label}
              className="px-4 py-2.5 bg-white/[0.08] border-2 border-[#FFC72C]/40 text-white text-sm font-semibold text-center transition-all duration-300 hover:scale-105 hover:border-[#FFC72C] hover:bg-white/[0.16] hover:shadow-[0_8px_20px_rgba(255,199,44,0.3)]"
            >
              {label}
            </div>
          ))}
        </div>
        <div className="relative w-px h-9 mt-1">
          <div className="absolute inset-0 w-px bg-gradient-to-b from-[#FFC72C]/70 to-[#FFC72C]/10" />
          <div
            className="absolute inset-0 w-px"
            style={{ borderLeft: "2px dashed #FFC72C", opacity: 0.9, animation: "dash-flow 0.6s linear infinite" }}
          />
        </div>
        <ChevronDown className="text-[#FFC72C] w-5 h-5 -mt-1" />
      </div>

      <div className="flex flex-col items-center">
        <span className="text-xs font-bold uppercase tracking-widest text-white/50 mb-3">Hosting Layer</span>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          <div
            data-testid="infra-diagram-node-cloud"
            className="flex items-center gap-3 px-5 py-4 bg-[#FFC72C] text-black font-bold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_10px_26px_rgba(255,199,44,0.55)]"
          >
            <ShieldCheck className="w-5 h-5 shrink-0" />
            Canadian Cloud (Now)
          </div>
          <div
            data-testid="infra-diagram-node-onnation"
            className="flex items-center gap-3 px-5 py-4 bg-white/[0.08] border-2 border-white/25 text-white font-bold transition-all duration-300 hover:scale-[1.03] hover:border-[#FFC72C] hover:bg-white/[0.14] hover:shadow-[0_10px_26px_rgba(255,199,44,0.3)]"
          >
            <Building2 className="w-5 h-5 shrink-0 text-[#FFC72C]" />
            On-Nation Rack (Future)
          </div>
        </div>
      </div>

      <div className="flex justify-center sm:justify-end sm:pr-2 mt-3">
        <div className="flex items-center gap-2.5 text-white/70 text-sm" data-testid="infra-diagram-node-partners">
          <Users className="w-4 h-4 text-[#FFC72C]" />
          Local IT &amp; Infrastructure Partners - install &amp; maintain
        </div>
      </div>

      <div className="mt-8 text-center">
        <button
          onClick={() => setShowTech(!showTech)}
          data-testid="infra-tech-details-toggle"
          className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-[#FFC72C] border-b-2 border-[#FFC72C]/40 pb-1 hover:border-[#FFC72C] transition-colors duration-300"
        >
          {showTech ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showTech ? "Hide" : "See"} Technical Details
        </button>
        {showTech && (
          <ul data-testid="infra-tech-details-content" className="mt-5 text-left max-w-xl mx-auto space-y-2.5">
            {INFRA_TECH_DETAILS.map((d, i) => (
              <li key={i} className="flex gap-2.5 text-white/80 text-base leading-relaxed">
                <Check className="w-4 h-4 text-[#FFC72C] shrink-0 mt-1" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TWO CREATIVE DIAGRAM OPTIONS - animated networking-style visuals for review
// ═══════════════════════════════════════════════════════════════════════════════
function InfraDiagramSignalPath() {
  return (
    <div data-testid="infra-diagram-option-signal" className="bg-black/15 border-2 border-white/10 p-8 md:p-10">
      <div className="flex flex-col md:flex-row items-center gap-8 md:gap-4">
        <div className="flex flex-col items-center gap-3 shrink-0">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full border-2 border-[#FFC72C]/60" style={{ animation: "pulse-ring 2.2s ease-out infinite" }} />
            <span className="absolute inset-0 rounded-full border-2 border-[#FFC72C]/60" style={{ animation: "pulse-ring 2.2s ease-out infinite", animationDelay: "1.1s" }} />
            <div className="relative w-16 h-16 rounded-full bg-[#FFC72C] flex items-center justify-center">
              <Lock className="w-7 h-7 text-black" />
            </div>
          </div>
          <div className="border-2 border-dashed border-[#FFC72C]/50 rounded-full px-4 py-1">
            <span className="text-xs font-bold uppercase tracking-widest text-[#FFC72C]">Within Canada</span>
          </div>
          <span className="text-white font-bold text-sm text-center leading-snug">Encrypted Cloud<br />(Now)</span>
        </div>

        <div className="relative flex-1 h-10 flex items-center w-full min-w-[6rem]">
          <div className="absolute left-0 right-0 h-px bg-white/20" />
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="absolute w-2.5 h-2.5 rounded-full bg-[#FFC72C] shadow-[0_0_10px_2px_rgba(255,199,44,0.7)]"
              style={{ top: "50%", marginTop: "-5px", animation: "packet-flow 2.4s linear infinite", animationDelay: `${i * 0.8}s` }}
            />
          ))}
        </div>

        <div className="flex flex-col items-center gap-3 shrink-0">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <div className="relative w-16 h-16 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-[#FFC72C]" />
            </div>
          </div>
          <div className="border-2 border-dashed border-white/30 rounded-full px-4 py-1">
            <span className="text-xs font-bold uppercase tracking-widest text-white/70">On Frog Lake Land</span>
          </div>
          <span className="text-white font-bold text-sm text-center leading-snug">On-Nation Rack<br />(Future)</span>
        </div>
      </div>
      <p className="text-center text-white/60 text-sm mt-8">
        Data flows secured, encrypted, and redundant - every step of the way home.
      </p>
    </div>
  );
}

const ORBIT_APPS = [
  { icon: Globe, angle: -90, label: "Website" },
  { icon: Archive, angle: 30, label: "Data Archives" },
  { icon: LayoutGrid, angle: 150, label: "Nation Platform" },
];

// Real, simplified Canada country outline (Natural Earth 1:50m boundary data, CC0 public domain
// via simplemaplab.com) - mainland + Vancouver Island, Newfoundland/Labrador, and Hudson Bay islands.
const CANADA_OUTLINE_PATH =
  "M77.2,328.7L38.9,277.0L25.1,289.3L13.5,269.2L0.0,269.9L0.0,112.6L40.4,131.8L35.8,123.4L46.4,112.0L48.0,120.6L79.6,101.5L54.9,130.7L64.8,117.0L70.5,121.5L85.4,110.6L91.5,93.5L107.4,118.1L115.6,102.4L117.2,118.4L133.1,109.3L182.9,128.5L190.1,137.2L181.9,148.2L217.5,144.9L237.0,173.8L232.3,148.2L247.9,133.5L227.0,139.2L245.0,127.2L272.0,149.7L297.9,143.4L306.2,151.9L297.8,137.9L316.6,140.1L317.9,170.0L320.2,144.4L334.4,133.0L326.3,129.4L333.8,116.1L312.6,101.2L313.3,79.3L326.2,62.6L347.6,101.8L338.3,112.3L355.5,116.2L357.2,139.9L363.7,120.6L370.5,126.5L377.2,160.0L394.7,130.0L390.2,108.7L412.2,112.8L420.1,130.6L411.0,136.3L418.6,161.9L405.0,174.3L393.0,164.9L401.7,177.1L381.8,171.7L387.0,178.7L376.5,192.1L348.6,181.7L379.5,194.5L371.9,211.1L352.9,220.8L332.6,211.7L353.6,225.1L336.1,239.6L325.1,267.0L323.8,287.0L328.1,297.5L336.3,291.7L338.9,315.5L354.5,311.6L391.2,338.7L412.1,338.8L416.1,372.6L433.5,389.1L439.9,372.2L431.0,343.7L453.2,322.7L439.4,292.3L448.0,273.8L442.1,240.9L473.2,238.0L489.2,257.5L503.0,259.7L494.6,273.7L503.9,283.8L498.2,291.2L510.6,291.5L506.0,302.7L527.4,295.7L536.5,269.4L565.2,338.9L587.8,344.7L567.2,360.5L582.3,349.0L598.7,359.6L599.8,373.6L590.8,381.3L569.0,394.7L523.9,395.2L501.6,416.8L492.1,414.1L500.8,417.9L494.3,427.9L451.3,463.1L410.0,481.8L411.0,446.1L370.0,415.7L347.9,418.3L322.4,404.4L126.0,408.5L113.5,387.9L93.5,384.7L100.6,378.5L93.4,380.9L92.9,373.0L100.5,374.5L98.3,365.4L90.7,377.6L87.7,353.5L75.5,355.9L77.2,328.8Z" +
  " M382.6,84.2L393.1,81.1L394.7,84.4L395.9,70.6L387.4,62.6L391.5,56.9L398.8,61.3L389.2,45.5L391.1,40.2L398.9,42.8L390.6,36.4L397.9,27.8L402.3,28.8L402.7,24.8L413.5,20.8L419.1,23.3L427.0,44.4L420.3,55.1L424.6,51.9L422.4,64.4L429.8,58.2L430.1,52.0L436.0,56.0L438.9,65.0L438.1,54.2L446.4,58.2L440.0,49.1L450.8,45.6L463.3,53.6L457.6,68.7L463.0,60.9L469.3,61.4L468.9,65.8L462.6,68.8L466.2,69.6L464.1,79.7L467.7,70.2L472.1,67.5L469.7,75.6L473.2,71.6L476.9,78.3L478.8,69.5L489.9,75.6L489.4,81.3L480.7,88.1L487.0,83.4L494.5,83.3L493.9,88.9L486.0,96.5L490.3,95.1L489.2,102.9L492.4,93.4L498.7,88.1L510.2,93.1L509.9,96.9L498.8,104.6L507.9,101.3L508.1,106.7L514.3,99.7L518.8,111.0L513.2,110.2L504.5,115.4L518.7,116.5L522.6,121.8L509.0,121.5L506.0,123.7L514.5,124.3L514.1,129.8L504.0,128.4L513.8,134.8L522.2,136.1L520.9,143.6L525.9,139.5L524.3,147.9L527.8,143.5L531.6,150.8L535.0,144.2L534.3,148.8L542.5,152.9L541.4,156.9L536.5,156.8L548.2,158.9L543.5,166.3L554.6,162.3L560.4,169.4L554.6,169.6L558.5,173.6L556.5,175.4L551.6,173.7L555.6,180.2L551.3,180.2L550.9,187.0L545.2,183.4L544.2,198.9L539.0,194.9L539.5,191.0L536.8,195.0L531.6,184.9L538.3,175.3L528.6,180.9L529.8,177.3L526.9,178.5L520.4,169.8L518.8,175.4L514.1,172.7L517.9,182.0L508.1,177.3L511.8,179.5L513.8,188.3L516.3,186.9L519.5,190.4L522.5,200.8L522.9,197.8L525.9,202.4L527.8,199.7L531.4,202.3L530.7,205.9L533.9,207.1L530.3,209.4L538.6,218.8L536.8,226.1L533.1,217.9L536.7,231.1L533.3,230.9L533.7,235.6L522.8,225.8L522.3,228.6L514.1,218.3L515.0,223.3L506.9,218.8L527.5,242.0L527.0,246.4L509.6,241.2L502.0,233.4L489.8,228.8L485.3,223.4L489.6,220.8L481.7,216.9L475.0,207.6L476.3,204.7L470.7,207.2L466.3,200.6L466.2,206.1L459.1,205.6L451.1,210.3L442.7,206.0L442.3,198.8L447.5,194.5L447.7,190.2L458.3,194.0L460.9,200.4L458.5,192.8L474.3,189.6L468.2,177.9L483.7,158.6L476.2,138.0L472.4,137.9L472.4,131.7L469.2,134.5L464.8,129.3L466.1,124.7L452.9,131.5L453.2,125.4L459.5,121.4L453.8,116.3L455.4,112.4L451.8,114.2L449.1,112.6L450.3,109.1L445.9,108.7L444.6,100.6L441.0,100.8L435.5,92.9L433.1,97.2L437.3,105.3L432.3,107.8L418.0,103.2L422.4,111.0L417.9,106.7L412.8,108.9L406.8,105.3L388.3,104.8L384.5,102.5L384.0,95.9L373.8,100.4L366.7,94.6L362.5,83.1L378.7,84.2L359.7,73.7L359.6,52.8L364.1,36.3L374.7,21.1L383.9,17.8L393.7,19.7L382.0,44.6L385.2,64.6L393.6,76.7L383.4,83.5Z" +
  " M186.2,48.6L192.8,47.2L194.8,40.1L198.6,39.2L209.1,45.7L206.2,55.4L216.5,47.0L213.3,38.7L217.9,39.3L226.7,49.5L233.4,70.7L236.9,64.7L230.4,35.3L231.9,30.4L238.3,34.2L240.3,32.5L250.2,44.0L257.5,71.8L256.6,83.1L263.1,92.3L266.7,94.9L266.9,91.9L280.6,102.7L281.7,111.3L273.0,108.7L269.9,114.6L264.2,112.3L266.4,121.6L273.2,115.9L275.7,122.0L268.0,129.0L252.4,127.2L253.0,124.0L245.1,122.4L241.5,116.9L236.0,125.6L221.7,131.9L196.0,135.4L192.0,121.8L172.2,117.2L167.6,103.6L185.7,99.1L206.5,99.1L191.6,91.1L164.6,92.4L159.8,83.7L180.7,73.6L162.2,75.9L160.1,74.3L163.6,70.0L154.8,67.6L160.2,56.2L159.1,49.9L171.8,37.6L187.7,30.9L189.5,39.9L186.4,47.8Z" +
  " M149.5,11.3L153.3,9.1L153.9,13.7L157.9,7.9L165.2,8.2L180.1,26.7L154.2,47.5L151.1,55.3L146.4,57.4L143.3,73.4L135.4,74.7L125.9,82.4L119.5,69.6L110.4,62.9L106.6,62.8L121.0,20.0L114.6,5.1L137.1,0.0L150.7,8.2L149.5,10.8Z" +
  " M519.5,447.9L517.8,447.9L517.3,443.6L514.7,442.3L514.7,428.5L511.1,425.7L506.7,427.2L504.6,424.6L499.2,432.3L497.2,440.5L493.2,446.9L489.9,446.7L488.6,449.5L466.1,449.6L474.2,445.4L477.0,439.5L484.4,434.0L495.6,429.0L503.0,419.3L511.7,412.3L530.7,405.5L539.6,409.2L540.0,410.4L537.8,410.0L539.7,411.6L539.0,414.5L532.6,418.7L527.9,417.0L522.4,418.7L528.4,419.9L530.1,422.4L534.4,420.6L536.5,421.8L532.2,428.3L535.6,428.7L537.6,437.1L550.2,443.1L555.8,440.9L560.1,443.9L559.3,446.0L562.3,446.7L544.2,452.7L544.2,454.7L539.4,454.1L539.5,456.2L531.0,464.1L526.5,461.3L528.3,453.9L526.7,454.5L538.3,446.2L540.5,449.4L540.8,447.4L545.9,446.0L535.3,446.1L539.2,441.2L537.0,440.1L536.0,443.2L528.2,447.4L527.2,445.4L524.3,448.7L519.8,448.2Z";

function OrbitCluster({ hubIcon: HubIcon, hubLabel, active, size = 224 }) {
  const speed = active ? 14 : 20;
  const hubSize = size * 0.36;
  const iconPx = size * 0.179;
  const r = size / 2 - iconPx / 2 - 4;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <div className={`absolute inset-0 rounded-full border-2 ${active ? "border-[#FFC72C]/70" : "border-white/25 border-dashed"}`} />
      <div className="absolute inset-0" style={{ animation: `orbit-spin ${speed}s linear infinite` }}>
        {ORBIT_APPS.map((s, i) => {
          const rad = (s.angle * Math.PI) / 180;
          const cx = size / 2 + r * Math.cos(rad) - iconPx / 2;
          const cy = size / 2 + r * Math.sin(rad) - iconPx / 2;
          const Icon = s.icon;
          return (
            <div
              key={i}
              className="absolute"
              style={{ left: cx, top: cy, width: iconPx, height: iconPx, animation: `orbit-spin-reverse ${speed}s linear infinite` }}
              title={s.label}
            >
              <div
                className={`rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-125 ${active ? "bg-[#FFC72C]" : "bg-white/15 border border-white/30"}`}
                style={{ width: iconPx, height: iconPx }}
              >
                <Icon className={active ? "text-black" : "text-white/70"} style={{ width: iconPx * 0.45, height: iconPx * 0.45 }} />
              </div>
            </div>
          );
        })}
      </div>
      <div
        className={`relative rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 ${active ? "bg-[#FFC72C] hover:shadow-[0_0_24px_rgba(255,199,44,0.6)]" : "bg-white/10 border-2 border-white/30 hover:border-[#FFC72C]"}`}
        style={{ width: hubSize, height: hubSize }}
      >
        <HubIcon className={active ? "text-black" : "text-[#FFC72C]"} style={{ width: hubSize * 0.4, height: hubSize * 0.4 }} />
      </div>
      <span className="absolute text-white font-bold text-xs sm:text-sm text-center w-full whitespace-nowrap" style={{ bottom: -26 }}>
        {hubLabel}
      </span>
    </div>
  );
}

function InfraDiagramOrbitingNetwork() {
  const clusterSize = 118;
  return (
    <div data-testid="infra-diagram-option-orbit" className="bg-black/15 border-2 border-white/10 p-6 md:p-10">
      <div className="relative w-full max-w-2xl mx-auto" style={{ aspectRatio: "600/482" }}>
        <svg viewBox="0 0 600 482" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full">
          <path d={CANADA_OUTLINE_PATH} fill="rgba(255,255,255,0.05)" stroke="#FFC72C" strokeOpacity="0.45" strokeWidth="2" />
          <text x="150" y="30" textAnchor="middle" fill="#FFC72C" fillOpacity="0.5" fontSize="15" letterSpacing="2" fontWeight="700">
            CANADA
          </text>
        </svg>

        {/* Frog Lake First Nation boundary nested inside Canada, around the On-Nation cluster */}
        <div
          className="absolute rounded-full border-2 border-dashed border-white/40"
          data-testid="infra-diagram-frog-lake-boundary"
          style={{ left: "29%", top: "72%", width: clusterSize * 1.6, height: clusterSize * 1.6, transform: "translate(-50%, -50%)" }}
        />
        <span
          className="absolute text-white/60 text-[10px] sm:text-xs font-bold uppercase tracking-wider whitespace-nowrap"
          style={{ left: "29%", top: `calc(72% - ${clusterSize * 0.8}px - 10px)`, transform: "translate(-50%, -100%)" }}
        >
          Frog Lake First Nation
        </span>

        <div className="absolute" style={{ left: "55%", top: "52%", transform: "translate(-50%, -50%)" }}>
          <OrbitCluster hubIcon={ShieldCheck} hubLabel="Canadian Cloud (Now)" active size={clusterSize} />
        </div>

        <div className="absolute" style={{ left: "29%", top: "72%", transform: "translate(-50%, -50%)" }}>
          <OrbitCluster hubIcon={Building2} hubLabel="On-Nation Rack (Future)" active={false} size={clusterSize} />
        </div>
      </div>

      <p className="text-center text-white/50 text-xs mt-10 sm:mt-8">
        Website · Data Archives · Nation Management Platform - orbiting both hosting options, always within Canada.
      </p>
      <p className="text-center text-white/60 text-sm mt-2">
        Two networks, one sovereignty commitment - control moves fully to Frog Lake when ready.
      </p>
    </div>
  );
}


function DataSovereigntySection() {
  return (
    <section className="py-24 px-4" style={{ backgroundColor: DARK_BLUE }} data-testid="data-sovereignty-section">
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <Chip>Data Sovereignty &amp; Infrastructure</Chip>
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-5">
            A Clear Path to Data Sovereignty
          </h2>
          <p className="text-white/85 text-xl max-w-2xl mx-auto leading-relaxed">
            Frog Lake's data stays secure today and comes home tomorrow - a two-phase approach that keeps development moving now while building toward full on-Nation control.
          </p>
        </Reveal>

        <Reveal delay={100} className="mb-16">
          <div className="rounded-2xl overflow-hidden border-2 border-[#FFC72C]/30 shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
            <img
              src={IMG.sovereignty}
              alt="On-site server room overlooking a lake in central Alberta"
              className="w-full h-[280px] sm:h-[380px] object-cover"
              data-testid="sovereignty-hero-image"
            />
          </div>
          <p className="text-center text-white/40 text-xs mt-3 uppercase tracking-widest">
            Concept visualization - on-Nation infrastructure, central Alberta
          </p>
        </Reveal>

        <Reveal>
          <InfraTimeline />
        </Reveal>

        <Reveal delay={150} className="mt-16">
          <InfraArchitectureDiagram />
        </Reveal>

        <Reveal delay={200} className="mt-20">
          <h3 className="text-center text-white text-2xl font-heading uppercase mb-8">How the Network Stays Sovereign</h3>
        </Reveal>

        <Reveal delay={250}>
          <InfraDiagramSignalPath />
        </Reveal>

        <Reveal delay={300} className="mt-10">
          <InfraDiagramOrbitingNetwork />
        </Reveal>

        <Reveal delay={200}>
          <p className="text-center text-white/70 text-lg max-w-2xl mx-auto leading-relaxed mt-16">
            Nothing here slows down today's work. The Nation's applications, archives, and platform keep moving forward now - while the path home to on-Nation infrastructure is already mapped.
          </p>
        </Reveal>
        <Conf />
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROPOSAL PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const Proposal = () => (
  <div className="font-sans bg-white overflow-x-hidden">

    {/* ── 1. HERO ─────────────────────────────────────────────────────────── */}
    <section className="flex flex-col overflow-hidden" style={{ minHeight: "100vh" }}>

      {/* ── Dark cinematic image area ── */}
      <div className="relative flex-1 flex flex-col items-center justify-center" style={{ minHeight: "68vh" }}>
        <img src={IMG.hero} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 30%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/72 via-black/62 to-black/88" />

        <div className="relative z-10 text-center px-6 max-w-4xl w-full mx-auto">
          <h1
            className="font-heading text-white uppercase leading-none tracking-tight mb-3"
            style={{ textShadow: "0 4px 22px rgba(0,0,0,0.8)", fontWeight: 800, fontSize: "clamp(2.5rem, calc((100vw - 3rem) / 7.1), 9.6rem)" }}
          >
            froglake.ca
          </h1>
          <h2
            className="font-heading text-[#FFC72C] uppercase leading-none mb-8"
            style={{ textShadow: "0 3px 18px rgba(0,0,0,0.7)", fontWeight: 800, fontSize: "clamp(2rem, calc((100vw - 3rem) / 7.1 * 0.63), 5.4rem)" }}
          >
            Reimagined
          </h2>
          <p
            className="text-white text-2xl sm:text-3xl max-w-xl mx-auto leading-relaxed mb-6 font-semibold"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.8)" }}
          >
            A modern digital home, and a growing platform, for a modern Nation
          </p>
          <div className="inline-flex items-center gap-2.5 px-5 sm:px-6 py-2.5 border-2 border-[#FFC72C]/70 rounded-full bg-white/10 backdrop-blur-sm max-w-[90%] mx-auto">
            <span className="w-2 h-2 rounded-full bg-[#FFC72C] shrink-0" />
            <p className="text-white text-sm sm:text-lg uppercase tracking-widest font-bold min-w-0">
              Prepared for Frog Lake First Nation<br />by Bear Valley Solutions
            </p>
          </div>
        </div>

        {/* Photo credit - bottom right */}
        <div className="absolute bottom-4 right-5 z-20 text-right">
          <p className="text-white/38 text-xs leading-tight">
            Photo:{" "}
            <a
              href="https://reimagine.ca/work/project/frog-lake-first-nations%E2%80%99-children-family-intervention-and-prevention-horse-healing-centre"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-photo-credit-link"
              className="underline underline-offset-2 hover:text-white/65 transition-colors"
            >
              Reimagine Architects
            </a>
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/45 animate-bounce z-20">
          <ChevronDown className="w-7 h-7" />
        </div>
      </div>

      {/* ── White logo bar ── */}
      <div className="bg-white border-t-4 border-[#FFC72C]">
        <div className="container mx-auto max-w-4xl flex items-center justify-between gap-6 px-8 py-8">
          {/* Frog Lake Cree Nation */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={IMG.mosaic1}
              alt="Frog Lake Cree Nation"
              className="max-h-[134px] w-auto max-w-full object-contain"
              style={{ maxWidth: "384px" }}
            />
          </div>

          <div className="w-px self-stretch bg-gray-200" />

          {/* Bear Valley Solutions */}
          <div className="flex-1 flex items-center justify-center">
            <img
              src={IMG.cta}
              alt="Bear Valley Solutions Inc."
              className="max-h-[134px] w-auto max-w-full object-contain"
              style={{ maxWidth: "384px" }}
            />
          </div>
        </div>
      </div>
    </section>

    {/* ── 2. VISION ───────────────────────────────────────────────────────── */}
    <section className="bg-[#F9F7F4] py-24 px-4">
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-[#0F2419] uppercase mb-5">
            A Website Worthy of What Frog Lake Has Built
          </h2>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto leading-relaxed">
            Frog Lake is one of the most active and successful First Nations in Canada. Its digital presence should reflect that - and grow into the Nation's own platform over time.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: "🏆", title: "Show the Nation's Success", desc: "Award-winning facilities, major infrastructure, and Nation-owned companies, presented with pride." },
            { icon: "📘", title: "Meet Members on Facebook",  desc: "Built around the channel the community already uses - publish once and reach everyone." },
            { icon: "🚀", title: "Grow Into a Platform",      desc: "The website becomes the front door to the Nation's own management systems, at founding-customer pricing." },
            { icon: "🌱", title: "Start Small",               desc: "A complete modern website, then add capability only as the Nation is ready." },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="group bg-white border-4 border-transparent border-t-[#FFC72C] p-7 h-full shadow-sm transition-all duration-300 hover:shadow-[0_12px_32px_rgba(255,199,44,0.45)] hover:-translate-y-2 hover:bg-[#FFF3D2] hover:border-[#FFC72C]">
                <div className="text-4xl mb-3 transition-transform duration-300 group-hover:scale-110">{c.icon}</div>
                <h3 className="font-heading text-xl uppercase text-[#0F2419] mb-3 transition-colors duration-300 group-hover:text-[#B8860B]">{c.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ── 3. ACHIEVEMENTS ─────────────────────────────────────────────────── */}
    <section className="relative py-24 px-4 overflow-hidden">
      <img src={IMG.story} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(10,31,20,0.72)" }} />
      <div className="relative z-10 container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <Chip>The Story Nobody Can Find</Chip>
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-5">
            Invisible Achievements. National News.
          </h2>
          <p className="text-white/85 text-xl max-w-2xl mx-auto leading-relaxed">
            The gap between what Frog Lake does and what froglake.ca shows is{" "}
            <span className="text-[#FFC72C] font-semibold">the opening.</span>
          </p>
        </Reveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mb-14">
          {[
            { value: "$36M",  label: "Horse Healing Centre",  desc: "Net-zero facility, winner of a Canadian Architect Award of Excellence" },
            { value: "$70M+", label: "Water System Upgrade",  desc: "Modern water infrastructure, built with Nation-owned trade partners" },
            { value: "600+",  label: "Nation-Owned Energy",   desc: "FLERC - first oil company owned by a Canadian First Nation, 600+ wells since 2000" },
            { value: "1st",   label: "Hemp Innovation",       desc: "Asinikahtamwak LP - a Nation-partnered hempcrete plant, show home underway" },
            { value: "🦬",    label: "The Buffalo Return",    desc: "Plains bison returned to Frog Lake land through the Buffalo Treaty and Parks Canada" },
            { value: "📺",    label: "In the News",           desc: 'CBC (2025): "Frog Lake is growing by leaps and bounds"' },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 70}>
              <div className="group backdrop-blur-sm bg-white/[0.08] border-2 border-white/20 p-6 h-full transition-all duration-300 hover:border-4 hover:border-[#FFC72C] hover:bg-white/[0.20] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)]">
                <div className="font-heading text-5xl text-[#FFC72C] mb-2 transition-transform duration-300 group-hover:scale-110">{s.value}</div>
                <h3 className="font-bold text-white text-base uppercase tracking-wider mb-2">{s.label}</h3>
                <p className="text-white/80 text-base leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="max-w-2xl mx-auto border-2 border-[#FFC72C]/35 p-1">
            <div className="aspect-video w-full">
              <iframe
                src="https://www.youtube.com/embed/4FpEEsE-AhA"
                title="Frog Lake on CBC Documentary"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="text-center text-white/65 text-base py-2.5 uppercase tracking-wider">
              ▶ Watch the Documentary - Frog Lake on CBC
            </p>
          </div>
        </Reveal>
        <Conf />
      </div>
    </section>

    {/* ── 4. BEFORE / AFTER ───────────────────────────────────────────────── */}
    <section className="bg-white py-24 px-4">
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-[#0F2419] uppercase mb-5">
            What's Holding froglake.ca Back Today
          </h2>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto leading-relaxed">
            The current froglake.ca is a Wix site that has not kept pace with the Nation. Every issue below is a quick win the new platform solves.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 border border-gray-200 shadow overflow-hidden">
          {/* Today */}
          <div className="bg-red-50 p-8 border-b md:border-b-0 md:border-r border-gray-200">
            <div className="flex items-center gap-3 mb-7">
              <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0">
                <X className="w-4 h-4" />
              </div>
              <h3 className="font-heading text-2xl uppercase text-red-700 tracking-wide">Today on froglake.ca</h3>
            </div>
            <ul className="space-y-4">
              {[
                '"Elections 2023" is still the first menu item',
                "Copyright still reads 2022",
                "Business Directory link loops back to the homepage",
                "Events run through a single Google Form",
                "Quick links (A to Z, FOI, FAQ) go nowhere",
                "One Facebook link in the footer, no integration",
                "Wix, no data control, no path to a Nation platform",
                "Cree appears only in the hero banner",
              ].map((item, i) => (
                <Reveal key={i} delay={i * 40} from="left">
                  <li className="group flex items-start gap-2.5 text-lg text-gray-700 border-2 border-transparent rounded px-3 py-2 -mx-3 transition-all duration-300 hover:text-red-700 hover:font-semibold hover:bg-red-100 hover:border-red-300 hover:shadow-md">
                    <X className="w-4 h-4 text-red-400 mt-1.5 shrink-0 transition-transform duration-200 group-hover:scale-125" />
                    {item}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          {/* Future */}
          <div className="bg-green-50 p-8">
            <div className="flex items-center gap-3 mb-7">
              <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center shrink-0">
                <Check className="w-4 h-4" />
              </div>
              <h3 className="font-heading text-2xl uppercase text-green-800 tracking-wide">The froglake.ca we build</h3>
            </div>
            <ul className="space-y-4">
              {[
                "Current, prioritized navigation the Nation controls in minutes",
                "Always current, updated by staff with no developer needed",
                "A real, searchable directory of Nation and member businesses",
                "Live events calendar that also publishes straight to Facebook",
                "Working self-serve member services that actually resolve",
                "Facebook woven through the site: feed, events, and sharing",
                "Canadian-hosted, OCAP-aligned, ready to become the hub",
                "Cree built into the structure of the site, English alongside",
              ].map((item, i) => (
                <Reveal key={i} delay={i * 40} from="right">
                  <li className="group flex items-start gap-2.5 text-lg text-gray-700 border-2 border-transparent rounded px-3 py-2 -mx-3 transition-all duration-300 hover:text-green-800 hover:font-semibold hover:bg-green-100 hover:border-green-400 hover:shadow-md">
                    <Check className="w-4 h-4 text-green-600 mt-1.5 shrink-0 transition-transform duration-200 group-hover:scale-125" />
                    {item}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ── 5. TWO JOBS ─────────────────────────────────────────────────────── */}
    <section className="py-24 px-4" style={{ backgroundColor: DARK_GREEN }}>
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-5">
            One Website. Two Jobs.
          </h2>
          <p className="text-white/85 text-xl max-w-xl mx-auto leading-relaxed">
            Purpose-built for how Frog Lake works - not an off-the-shelf platform the Nation has to bend around.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal delay={0} from="left">
            <div className="group border-4 border-transparent border-t-[#FFC72C] bg-white/[0.07] p-8 h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)] hover:bg-white/[0.18] hover:border-[#FFC72C]">
              <div className="inline-block bg-[#FFC72C] text-black text-sm font-bold uppercase tracking-widest px-3 py-1 mb-5 transition-transform duration-300 group-hover:scale-105">
                Outward
              </div>
              <h3 className="font-heading text-2xl text-white uppercase mb-4">The Outward Storefront</h3>
              <p className="text-white/85 text-lg leading-relaxed group-hover:text-white transition-colors duration-300">
                A modern professional site for investors, partners, governments, media, and members - a sophisticated Nation open for business, with showcase projects and credibility at first click.
              </p>
            </div>
          </Reveal>
          <Reveal delay={140} from="right">
            <div className="group border-4 border-transparent border-t-[#005696] bg-white/[0.07] p-8 h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(0,86,150,0.5)] hover:bg-white/[0.18] hover:border-[#005696]">
              <div className="inline-block bg-[#005696] text-white text-sm font-bold uppercase tracking-widest px-3 py-1 mb-5 transition-transform duration-300 group-hover:scale-105">
                Inward
              </div>
              <h3 className="font-heading text-2xl text-white uppercase mb-4">The Inward Hub</h3>
              <p className="text-white/85 text-lg leading-relaxed group-hover:text-white transition-colors duration-300">
                One front door for every department. Staff log in, each department manages its own content - events, news, jobs, programs - with no waiting on outside companies for updates.
              </p>
            </div>
          </Reveal>
        </div>
        <Conf />
      </div>
    </section>

    {/* ── 6. FACEBOOK ─────────────────────────────────────────────────────── */}
    <section className="bg-[#F9F7F4] py-24 px-4">
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-[#0F2419] uppercase mb-5">
            Meet the Nation Where It Already Is
          </h2>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto leading-relaxed">
            Facebook is the Nation's main channel today. We build around that, not against it.
          </p>
        </Reveal>

        <Reveal className="mb-14">
          <div className="bg-white border border-gray-200 p-10 shadow-sm">
            <p className="text-center text-base font-bold uppercase tracking-widest text-gray-400 mb-8">
              Publish once, everywhere:
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {["Create Post", "→", "Website", "→", "FB Page", "→", "FB Event"].map((item, i) =>
                item === "→"
                  ? <span key={i} className="text-gray-300 text-2xl font-thin hidden sm:block">→</span>
                  : <div key={i} className="bg-[#FFC72C] text-black font-bold text-base px-5 py-2.5 uppercase tracking-wider border-2 border-transparent transition-all duration-300 hover:scale-110 hover:bg-[#0F2419] hover:text-[#FFC72C] hover:border-[#FFC72C] hover:shadow-[0_8px_20px_rgba(255,199,44,0.5)]">{item}</div>
              )}
            </div>
            <p className="text-center text-lg text-gray-500 mt-8 italic">
              One action, three destinations, one permanent record.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: "📊", title: "Publish Once, Everywhere", desc: "Website, Facebook page post, and Facebook event - one action, three destinations, one permanent record." },
            { icon: "📈", title: "Reach You Can Report",     desc: "Facebook Insights plus site analytics - leadership sees real numbers at every Council meeting." },
            { icon: "🔗", title: "Room to Grow",             desc: "Instagram and a Nation LinkedIn can join the same publish flow later, with no rebuild required." },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="group bg-white border-2 border-gray-200 p-7 h-full shadow-sm transition-all duration-300 hover:border-4 hover:border-[#FFC72C] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.45)] hover:bg-[#FFF3D2]">
                <div className="text-4xl mb-3 transition-transform duration-300 group-hover:scale-110">{c.icon}</div>
                <h3 className="font-heading text-xl text-[#0F2419] uppercase mb-3 transition-colors duration-300 group-hover:text-[#B8860B]">{c.title}</h3>
                <p className="text-lg text-gray-600 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* ── 7. FOUNDATION ───────────────────────────────────────────────────── */}
    <section className="py-24 px-4" style={{ backgroundColor: DARK_BLUE }}>
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <Chip>Website Base</Chip>
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-5">
            The Foundation
          </h2>
          <p className="text-white/85 text-xl max-w-xl mx-auto leading-relaxed">
            A modern, professional website plus the admin foundation everything else builds on.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { icon: "📱", title: "Mobile-First Website",     desc: "Fast and professional on every phone and screen" },
            { icon: "🏛️", title: "Nation Showcase Pages",    desc: "Projects and businesses presented with pride" },
            { icon: "✏️", title: "Staff Content Management", desc: "No developer needed - staff update everything" },
            { icon: "📅", title: "Events Calendar",          desc: "Categories, signup, and Facebook publishing built in" },
            { icon: "🔒", title: "Secure Admin Access",      desc: "Role-based control, cloud hosting, and SSL included" },
            { icon: "💼", title: "Employment Postings",      desc: "Nation opportunities visible to members and the public" },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 70}>
              <div className="group border-2 bg-white/[0.05] border-white/12 p-7 h-full transition-all duration-300 hover:border-4 hover:border-[#FFC72C] hover:bg-white/[0.22] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)]">
                <div className="text-4xl mb-3 transition-transform duration-300 group-hover:scale-110">{c.icon}</div>
                <h3 className="font-heading text-xl uppercase text-[#FFC72C] mb-2">{c.title}</h3>
                <p className="text-lg text-white/80 leading-relaxed group-hover:text-white transition-colors duration-300">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Conf />
      </div>
    </section>

    {/* ── 8. COMMUNITY MODULES ────────────────────────────────────────────── */}
    <section className="bg-white py-24 px-4">
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-[#0F2419] uppercase mb-5">
            Community &amp; Engagement Modules
          </h2>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto leading-relaxed">
            One-time additions that extend the website. Select any combination - each module integrates seamlessly with the base platform.
          </p>
        </Reveal>

        <Reveal>
          <div className="border border-gray-200 shadow overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0F2419]">
                  <th className="text-left px-6 py-4 text-white font-heading uppercase tracking-wider text-base w-2/5">Module</th>
                  <th className="text-left px-6 py-4 text-white font-heading uppercase tracking-wider text-base">What It Does</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { mod: "Facebook Events Publishing",       desc: "Post once; publishes to the website and Facebook together" },
                  { mod: "Social Feed and News Syndication", desc: "Facebook feed embedded; news shared automatically" },
                  { mod: "Business Directory",               desc: "A real, searchable directory of Nation and member businesses" },
                  { mod: "Project Showcase Gallery",         desc: "Rich pages for major projects: photos, video, milestones" },
                  { mod: "Cree Language Structure",          desc: "Syllabics and English built into the site structure" },
                  { mod: "SEO and Search Visibility",        desc: "The Nation's own site tells its story first in search" },
                ].map((row, i) => (
                  <tr key={i} className={`group transition-colors duration-200 hover:bg-[#FFE29A] border-l-4 border-transparent hover:border-l-[#FFC72C] ${i % 2 === 0 ? "bg-white" : "bg-[#F9F7F4]"}`}>
                    <td className="px-6 py-5 font-bold text-lg text-[#0F2419] border-b border-gray-100 transition-colors duration-200 group-hover:text-[#8a6200]">{row.mod}</td>
                    <td className="px-6 py-5 text-lg text-gray-600 border-b border-gray-100 group-hover:text-gray-800">{row.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>

    {/* ── 9. FOUNDING CUSTOMER ────────────────────────────────────────────── */}
    <section className="relative py-24 px-4 overflow-hidden">
      <img src={IMG.founding} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(10,31,20,0.68)" }} />
      <div className="relative z-10 container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <Chip>Frog Lake as Flagship Partner</Chip>
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-5">
            Real Software, Ready Now
          </h2>
          <p className="text-white/85 text-xl max-w-2xl mx-auto leading-relaxed">
            Bear Valley Solutions has already built the modern website foundation Frog Lake needs.{" "}
            <span className="text-[#FFC72C] font-semibold">froglake.ca</span> goes from concept to live site quickly, shaped around how the Nation actually works.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            { icon: "🔨", title: "Already Built, Not From Scratch", desc: "The website platform exists and is being refined now, not designed from a blank page." },
            { icon: "⚡", title: "Live Quickly, Not Eventually",    desc: "A streamlined setup process brings froglake.ca online in weeks, not years." },
            { icon: "⭐", title: "Shaped by Frog Lake's Needs",       desc: "Every page and feature reflects how the Nation actually works, not a generic template built for anyone." },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="group border-2 bg-white/[0.09] border-[#FFC72C]/28 p-7 h-full transition-all duration-300 hover:border-4 hover:border-[#FFC72C] hover:bg-white/[0.22] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)]">
                <div className="text-4xl mb-3 transition-transform duration-300 group-hover:scale-110">{c.icon}</div>
                <h3 className="font-heading text-xl uppercase text-[#FFC72C] mb-3">{c.title}</h3>
                <p className="text-lg text-white/85 leading-relaxed group-hover:text-white transition-colors duration-300">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Conf />
      </div>
    </section>

    {/* ── 10. PLATFORM APPS (interactive diagram) ─────────────────────────── */}
    <PlatformAppsSection />

    {/* ── 11. DATA SOVEREIGNTY & INFRASTRUCTURE ───────────────────────────── */}
    <DataSovereigntySection />

    {/* ── 12. GROWTH ROADMAP ──────────────────────────────────────────────── */}
    <section className="py-24 px-4" style={{ backgroundColor: DARK_GREEN }}>
      <div className="container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-5">
            Start Small. Grow With Purpose.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { num: "01", title: "Website Base",      topClass: "border-t-[#FFC72C]", hoverClass: "hover:border-[#FFC72C]", desc: "A complete, modern, mobile-first website - the foundation everything else builds on." },
            { num: "02", title: "Community Modules", topClass: "border-t-[#005696]", hoverClass: "hover:border-[#005696]", desc: "Facebook publishing, business directory, project showcase, Cree language, and SEO." },
            { num: "03", title: "Hub Login",         topClass: "border-t-[#FFC72C]", hoverClass: "hover:border-[#FFC72C]", desc: "One front door for every department - staff manage their own content through a single secure login." },
            { num: "04", title: "Platform Apps",     topClass: "border-t-[#005696]", hoverClass: "hover:border-[#005696]", desc: "Grants, housing, economic development, archives, and more - connected at founding-customer rates." },
          ].map((step, i) => (
            <Reveal key={i} delay={i * 110}>
              <div className={`group border-4 border-transparent ${step.topClass} bg-white/[0.05] p-7 h-full transition-all duration-300 hover:bg-white/[0.22] ${step.hoverClass} hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)]`}>
                <div className="font-heading text-6xl text-white/15 mb-4 transition-colors duration-300 group-hover:text-white/25">{step.num}</div>
                <h3 className="font-heading text-xl uppercase text-[#FFC72C] mb-3">{step.title}</h3>
                <p className="text-lg text-white/80 leading-relaxed group-hover:text-white transition-colors duration-300">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Conf />
      </div>
    </section>

    {/* ── 13. WHY THIS MATTERS ────────────────────────────────────────────── */}
    <section className="relative py-24 px-4 overflow-hidden">
      <img src={IMG.matters} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(10,31,20,0.66)" }} />
      <div className="relative z-10 container mx-auto max-w-5xl">
        <Reveal className="text-center mb-14">
          <Chip>Why This Matters</Chip>
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase">
            Professional. Sophisticated. Modern. Sovereign.
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: "👥", title: "Members",                desc: "Events, news, jobs, and services in one place - on their phones, connected to Facebook." },
            { icon: "🎯", title: "Leadership",             desc: "A digital presence that matches what the Nation has built, plus monthly reporting at every Council meeting." },
            { icon: "🤝", title: "Investors and Partners", desc: "Credibility at first click - projects and capability on full display." },
            { icon: "🏢", title: "Departments",            desc: "Own their content and systems through one login. Reach members without gatekeepers." },
            { icon: "🌍", title: "The Nation's Future",    desc: "A platform the Nation owns and governs - on the Nation's land, on the Nation's terms." },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="group border-2 border-white/18 p-7 h-full transition-all duration-300 hover:border-4 hover:border-[#FFC72C] hover:bg-white/[0.24] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)]">
                <div className="text-4xl mb-3 transition-transform duration-300 group-hover:scale-110">{c.icon}</div>
                <h3 className="font-heading text-xl uppercase text-[#FFC72C] mb-3">{c.title}</h3>
                <p className="text-lg text-white/85 leading-relaxed group-hover:text-white transition-colors duration-300">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Conf />
      </div>
    </section>

    {/* ── 14. CTA - READY TO MOVE FORWARD ────────────────────────────────── */}
    <section className="relative py-28 px-4 overflow-hidden">
      <img src={IMG.cta} alt="" className="absolute inset-0 w-full h-full object-cover" />
      {/* Overlay - lightened for a more inviting closing slide while keeping text readable */}
      <div className="absolute inset-0" style={{ backgroundColor: "rgba(8, 26, 16, 0.76)" }} />
      <div className="relative z-10 container mx-auto max-w-5xl">
        <Reveal className="text-center mb-12">
          <Chip>Next Steps</Chip>
          <h2 className="font-heading text-5xl sm:text-6xl text-white uppercase mb-6">
            Ready to Move Forward
          </h2>
          <p className="text-white/90 text-xl max-w-2xl mx-auto leading-relaxed">
            We are ready when Frog Lake is. The platform is built, the pricing is set, and the founding-customer window is open. This is the moment to close the gap between what Frog Lake has built and the digital presence the Nation deserves.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {[
            { icon: "👁️", title: "Review Vision",     desc: "Align leadership on goals" },
            { icon: "✅", title: "Confirm Priorities", desc: "Set module and app order" },
            { icon: "✍️", title: "Approve Website",   desc: "Greenlight project and start work" },
            { icon: "🖥️", title: "Preview Homepage",  desc: "Live homepage demo in two weeks" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="group border-2 bg-white/[0.08] border-white/30 p-6 text-center transition-all duration-300 hover:border-4 hover:border-[#FFC72C] hover:bg-white/[0.24] hover:-translate-y-2 hover:shadow-[0_12px_32px_rgba(255,199,44,0.4)]">
                <div className="text-3xl mb-2 transition-transform duration-300 group-hover:scale-110">{s.icon}</div>
                <h3 className="font-heading text-base uppercase text-[#FFC72C] mb-1">{s.title}</h3>
                <p className="text-base text-white/85">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl mx-auto">
            {[
              { value: "Website Base",  label: "The Foundation" },
              { value: "2 Weeks",       label: "Live Preview from kickoff" },
              { value: "Founding Rate", label: "Best pricing, always" },
            ].map((f, i) => (
              <div key={i} className="bg-[#FFC72C] border-4 border-transparent p-5 text-center transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:border-black/30 hover:bg-[#FFD65C]">
                <div className="font-heading text-2xl text-black">{f.value}</div>
                <div className="text-sm text-black/65 mt-1 uppercase tracking-wider">{f.label}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal className="flex justify-center mt-10">
          <Link
            to="/proposal/budget"
            data-testid="view-build-proposal-button"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#FFC72C] text-black font-bold uppercase tracking-wide text-sm hover:bg-[#FFD65C] hover:scale-105 transition-all duration-300"
          >
            View Build Proposal
          </Link>
        </Reveal>

        {/* Closing brand mark - smaller logo pairing for a clean, balanced closing slide */}
        <Reveal>
          <div className="mt-14 flex items-center justify-center" data-testid="cta-closing-logos">
            <div className="bg-white/95 rounded-md px-6 py-3.5 flex items-center gap-5 shadow-xl">
              <img src={IMG.mosaic1} alt="Frog Lake Cree Nation" className="h-9 w-auto object-contain" style={{ maxWidth: "150px" }} />
              <div className="w-px h-8 bg-gray-300" />
              <img src={IMG.cta} alt="Bear Valley Solutions Inc." className="h-9 w-auto object-contain" style={{ maxWidth: "150px" }} />
            </div>
          </div>
        </Reveal>

        <Conf />
      </div>
    </section>

    {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
    <footer className="bg-[#060F09] py-10 px-4">
      <div className="container mx-auto max-w-5xl text-center">
        <p className="text-white/30 text-xs uppercase tracking-widest mb-4">
          © 2025 Bear Valley Solutions · Prepared for Frog Lake First Nations · All Rights Reserved
        </p>
        <Link
          to="/"
          data-testid="proposal-footer-back-link"
          className="inline-flex items-center gap-1.5 text-white/45 text-sm hover:text-white/80 transition-colors"
        >
          ← Back to froglake.ca
        </Link>
      </div>
    </footer>
  </div>
);

export default Proposal;
